import { useEffect, useState, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Button, Input, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell } from "@heroui/react";
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'react-toastify';

type LibComponent = { id: number; type: string; description?: string };

const InsuranceComponentsLibraryTab = () => {
  const { session } = useSession();
  const [items, setItems] = useState<LibComponent[]>([]);
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/insurance/policies/components-library`, { method: 'GET' }, session);
      if (!res.ok) throw new Error('Failed');
      setItems(await res.json());
    } catch { toast.error('Kon componenten niet laden'); }
  }, [session]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!type.trim()) { toast.error('Type is verplicht'); return; }
    setIsSaving(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/insurance/policies/components-library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: type.trim(), description: description.trim() || undefined })
      }, session);
      if (!res.ok) throw new Error('Failed');
      setType(''); setDescription('');
      await load();
      toast.success('Component toegevoegd');
    } catch { toast.error('Toevoegen mislukt'); } finally { setIsSaving(false); }
  };

  const save = async (item: LibComponent) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/insurance/policies/components-library/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      }, session);
      if (!res.ok) throw new Error('Failed');
      toast.success('Opgeslagen');
      await load();
    } catch { toast.error('Opslaan mislukt'); }
  };

  const remove = async (id: number) => {
    if (!confirm('Verwijderen?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/insurance/policies/components-library/${id}`, { method: 'DELETE' }, session);
      if (!res.ok) throw new Error('Failed');
      toast.success('Verwijderd');
      await load();
    } catch { toast.error('Verwijderen mislukt'); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input label="Type" placeholder="Bijv. Brand" value={type} onChange={(e) => setType(e.target.value)} />
        <Input label="Beschrijving (optioneel)" value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className="flex items-end">
          <Button color="primary" className="bg-moss-primary text-white" startContent={<Plus />} isLoading={isSaving} onPress={create}>Toevoegen</Button>
        </div>
      </div>

      <Table aria-label="Componenten bibliotheek">
        <TableHeader>
          <TableColumn>Type</TableColumn>
          <TableColumn>Beschrijving</TableColumn>
          <TableColumn>Acties</TableColumn>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell>
                <Input size="sm" value={it.type} onChange={(e) => setItems(prev => prev.map(x => x.id === it.id ? { ...x, type: e.target.value } : x))} />
              </TableCell>
              <TableCell>
                <Input size="sm" value={it.description || ''} onChange={(e) => setItems(prev => prev.map(x => x.id === it.id ? { ...x, description: e.target.value } : x))} />
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="flat" startContent={<PencilSimple />} onPress={() => save(it)}>Opslaan</Button>
                  <Button size="sm" color="danger" variant="flat" startContent={<Trash />} onPress={() => remove(it.id)}>Verwijder</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default InsuranceComponentsLibraryTab;


